import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { MarkdownRenderer } from './MarkdownRenderer';
import { evaluateResponse, ResponseEvaluation } from '../utils/responseEvaluator';

interface QuickPreviewModalProps {
  isOpen: boolean;
  isLoading: boolean;
  result: string;
  onClose: () => void;
}

export const QuickPreviewModal: React.FC<QuickPreviewModalProps> = ({
  isOpen,
  isLoading,
  result,
  onClose
}) => {
  const { t } = useTranslation();
  const { isPortuguese } = useLanguage();
  const [viewMode, setViewMode] = useState<'markdown' | 'raw'>('markdown');

  // Evaluate response when result changes
  const evaluation: ResponseEvaluation = useMemo(() => {
    return result ? evaluateResponse(result) : {
      score: 0,
      quality: 'ruim',
      qualityEn: 'poor',
      details: {
        length: 0,
        hasStructure: false,
        hasSpecificInfo: false,
        coherence: 0,
        completeness: 0
      }
    };
  }, [result]);

  // Close modal with Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  // Quality indicator component
  const QualityIndicator: React.FC<{ evaluation: ResponseEvaluation }> = ({ evaluation }) => {
    const qualityColor = evaluation.quality === 'ótima' ? 'green' : evaluation.quality === 'boa' ? 'yellow' : 'red';
    const qualityText = isPortuguese ? evaluation.quality : evaluation.qualityEn;
    
    return (
      <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-200">
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${
            qualityColor === 'green' ? 'bg-green-500' : 
            qualityColor === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
          }`}></div>
          <span className="text-sm font-medium text-gray-700">
            {isPortuguese ? 'Qualidade' : 'Quality'}:
          </span>
          <span className={`text-sm font-bold capitalize ${
            qualityColor === 'green' ? 'text-green-700' : 
            qualityColor === 'yellow' ? 'text-yellow-700' : 'text-red-700'
          }`}>
            {qualityText}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500">
            {evaluation.score}%
          </span>
          <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-500 ${
                qualityColor === 'green' ? 'bg-green-500' : 
                qualityColor === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${evaluation.score}%` }}
            />
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-blue-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  {isPortuguese ? 'Pré-visualização Rápida' : 'Quick Preview'}
                </h2>
                <p className="text-sm text-gray-600">
                  {isPortuguese ? 'Resultado do seu prompt' : 'Your prompt result'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-white/50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative mb-4">
                {/* Animated loading spinner */}
                <div className="w-12 h-12 border-4 border-blue-100 border-top-blue-500 rounded-full animate-spin"></div>
                <div className="absolute inset-0 w-12 h-12 border-4 border-transparent border-top-blue-300 rounded-full animate-spin animate-reverse"></div>
              </div>
              <div className="text-center">
                <p className="text-lg font-medium text-gray-700 mb-2">
                  {isPortuguese ? 'Processando seu prompt...' : 'Processing your prompt...'}
                </p>
                <p className="text-sm text-gray-500">
                  {isPortuguese ? 'Aguarde alguns segundos' : 'Please wait a few seconds'}
                </p>
              </div>
              
              {/* Elegant loading bars */}
              <div className="mt-6 w-full max-w-xs">
                <div className="flex space-x-1">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="flex-1 h-1 bg-blue-200 rounded-full overflow-hidden"
                    >
                      <div
                        className="h-full bg-blue-500 rounded-full animate-pulse"
                        style={{
                          animationDelay: `${i * 0.2}s`,
                          animationDuration: '1.5s'
                        }}
                      ></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {result ? (
                <>
                  {/* Quality Evaluation */}
                  <QualityIndicator evaluation={evaluation} />
                  
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-800 flex items-center">
                      <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {isPortuguese ? 'Resultado' : 'Result'}
                    </h3>
                    <div className="flex items-center space-x-2">
                      {/* View Mode Toggle */}
                      <div className="flex bg-gray-100 rounded-lg p-1">
                        <button
                          onClick={() => setViewMode('markdown')}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            viewMode === 'markdown'
                              ? 'bg-white text-gray-700 shadow-sm'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          MD
                        </button>
                        <button
                          onClick={() => setViewMode('raw')}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            viewMode === 'raw'
                              ? 'bg-white text-gray-700 shadow-sm'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                          </svg>
                          RAW
                        </button>
                      </div>
                      
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(result);
                          // Could add a toast notification here
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700 bg-white px-2 py-1 rounded border border-gray-200 hover:border-gray-300 transition-colors"
                      >
                        {isPortuguese ? 'Copiar' : 'Copy'}
                      </button>
                    </div>
                  </div>
                  <div className="max-w-none">
                    {viewMode === 'markdown' ? (
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <MarkdownRenderer content={result} />
                      </div>
                    ) : (
                      <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                        <pre className="whitespace-pre-wrap text-sm text-gray-100 font-mono leading-relaxed overflow-x-auto">
                          {result}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-600">
                    {isPortuguese ? 'Nenhum resultado obtido' : 'No result obtained'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!isLoading && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex justify-between items-center">
            <div className="text-xs text-gray-500">
              {isPortuguese ? 'Pressione ESC para fechar' : 'Press ESC to close'}
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
            >
              {isPortuguese ? 'Fechar' : 'Close'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};